import { BigNumber } from '@ethersproject/bignumber'
import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { decodeFunctionResult, encodeFunctionData, type Abi } from 'viem'

import { useAppChainId } from '../../domain/wallet'
import { readBalancesUiDebug } from '../../utils/debugReadBalancesUi'
import { useBlockNumber } from '../application/hooks'
import { AppDispatch, AppState } from '../index'
import {
  addMulticallListeners,
  Call,
  removeMulticallListeners,
  parseCallKey,
  toCallKey,
  ListenerOptions,
} from './actions'
import type { ReadContractBinding } from './contractBinding'
import {
  readImmutableCallCache,
  writeImmutableCallCache,
} from './immutableCallCache'

export interface Result extends ReadonlyArray<any> {
  readonly [key: string]: any
}

type MethodArg = string | number | BigNumber | bigint
type MethodArgs = Array<MethodArg | MethodArg[]>

type OptionalMethodInputs = Array<MethodArg | MethodArg[] | undefined> | undefined

function isMethodArg(x: unknown): x is MethodArg {
  return ['string', 'number', 'bigint'].includes(typeof x) || BigNumber.isBigNumber(x as any)
}

function isValidMethodArgs(x: unknown): x is MethodArgs | undefined {
  return (
    x === undefined ||
    (Array.isArray(x) && x.every((xi) => isMethodArg(xi) || (Array.isArray(xi) && xi.every(isMethodArg))))
  )
}

/** Convierte argumentos del estilo ethers/viem para `encodeFunctionData`. */
function toEncodeArgs(inputs: MethodArgs | undefined): readonly unknown[] {
  if (!inputs || inputs.length === 0) return []
  return inputs.map((a) => {
    if (BigNumber.isBigNumber(a)) return BigInt(a.toString())
    if (Array.isArray(a)) return a.map((x) => (BigNumber.isBigNumber(x) ? BigInt(x.toString()) : x))
    return a
  })
}

interface CallResult {
  readonly valid: boolean
  readonly data: string | undefined
  readonly blockNumber: number | undefined
}

const INVALID_RESULT: CallResult = { valid: false, blockNumber: undefined, data: undefined }

export const NEVER_RELOAD: ListenerOptions = {
  blocksPerFetch: Infinity,
}

function useCallsData(calls: (Call | undefined)[], options?: ListenerOptions): CallResult[] {
  const chainId = useAppChainId()
  const callResults = useSelector<AppState, AppState['multicall']['callResults']>(
    (state) => state.multicall.callResults,
  )
  const dispatch = useDispatch<AppDispatch>()

  const serializedCallKeys: string = useMemo(
    () =>
      JSON.stringify(
        calls
          ?.filter((c): c is Call => Boolean(c))
          ?.map(toCallKey)
          ?.sort() ?? [],
      ),
    [calls],
  )

  useEffect(() => {
    const callKeys: string[] = JSON.parse(serializedCallKeys)
    if (!chainId || callKeys.length === 0) return undefined
    const parsedCalls = callKeys.map((key) => parseCallKey(key))
    dispatch(
      addMulticallListeners({
        chainId,
        calls: parsedCalls,
        options,
      }),
    )

    return () => {
      dispatch(
        removeMulticallListeners({
          chainId,
          calls: parsedCalls,
          options,
        }),
      )
    }
  }, [chainId, dispatch, options, serializedCallKeys])

  return useMemo(
    () =>
      calls.map<CallResult>((call) => {
        if (!chainId || !call) return INVALID_RESULT

        const result = callResults[chainId]?.[toCallKey(call)]
        let data
        if (result?.data && result?.data !== '0x') {
          data = result.data
        }

        if (!data && options?.blocksPerFetch === Infinity) {
          const cached = readImmutableCallCache(chainId, call)
          if (cached) {
            return { valid: true, data: cached.data, blockNumber: cached.blockNumber }
          }
        }

        return { valid: true, data, blockNumber: result?.blockNumber }
      }),
    [callResults, calls, chainId, options?.blocksPerFetch],
  )
}

interface CallState {
  readonly valid: boolean
  readonly result: Result | undefined
  readonly loading: boolean
  readonly syncing: boolean
  readonly error: boolean
}

const INVALID_CALL_STATE: CallState = { valid: false, result: undefined, loading: false, syncing: false, error: false }
const LOADING_CALL_STATE: CallState = { valid: true, result: undefined, loading: true, syncing: true, error: false }

function normalizeDecoded(methodName: string, decoded: unknown): Result {
  if (decoded === undefined || decoded === null) return [] as unknown as Result
  if (methodName === 'getReserves') {
    if (Array.isArray(decoded)) {
      return {
        reserve0: decoded[0],
        reserve1: decoded[1],
        blockTimestampLast: decoded[2],
      } as unknown as Result
    }
    return decoded as Result
  }
  if (Array.isArray(decoded)) return decoded as Result
  return [decoded] as unknown as Result
}

const decodeErrorLogAtByMethod = new Map<string, number>()
const DECODE_ERROR_LOG_MIN_MS = 15_000

function maybeLogMulticallDecodeError(methodName: string, data: string | undefined, err: unknown): void {
  const now = Date.now()
  const prev = decodeErrorLogAtByMethod.get(methodName) ?? 0
  if (now - prev < DECODE_ERROR_LOG_MIN_MS) return
  decodeErrorLogAtByMethod.set(methodName, now)
  const dataPreview =
    typeof data === 'string' && data.length > 2 ? `${data.slice(0, 66)}${data.length > 66 ? '…' : ''}` : data
  readBalancesUiDebug('multicall.toCallState.decodeError', {
    methodName,
    dataPreview,
    message: err instanceof Error ? err.message : String(err)
  })
}

function toCallState(
  callResult: CallResult | undefined,
  abi: Abi | undefined,
  methodName: string,
  latestBlockNumber: number | undefined,
): CallState {
  if (!callResult) return INVALID_CALL_STATE
  const { valid, data, blockNumber } = callResult
  if (!valid) return INVALID_CALL_STATE
  if (valid && !blockNumber) return LOADING_CALL_STATE
  if (!abi) return LOADING_CALL_STATE
  const success = data && data.length > 2
  const syncing = typeof latestBlockNumber === 'number' ? (blockNumber ?? 0) < latestBlockNumber : false
  let result: Result | undefined = undefined
  if (success && data) {
    try {
      const decoded = decodeFunctionResult({
        abi,
        functionName: methodName,
        data: data as `0x${string}`,
      })
      result = normalizeDecoded(methodName, decoded)
    } catch (decodeErr) {
      maybeLogMulticallDecodeError(methodName, data, decodeErr)
      return {
        valid: true,
        loading: false,
        error: true,
        syncing,
        result,
      }
    }
  }
  return {
    valid: true,
    loading: false,
    syncing,
    result: result,
    error: !success,
  }
}

export function useSingleContractMultipleData(
  contract: ReadContractBinding | null | undefined,
  methodName: string,
  callInputs: OptionalMethodInputs[],
  options?: ListenerOptions,
): CallState[] {
  const calls = useMemo(
    () =>
      contract && callInputs && callInputs.length > 0
        ? callInputs.map<Call>((inputs) => {
            if (!isValidMethodArgs(inputs)) return { address: contract.address, callData: '0x' }
            const callData = encodeFunctionData({
              abi: contract.abi,
              functionName: methodName,
              args: toEncodeArgs(inputs),
            })
            return {
              address: contract.address,
              callData,
            }
          })
        : [],
    [callInputs, contract, methodName],
  )

  const results = useCallsData(calls, options)

  const latestBlockNumber = useBlockNumber()

  return useMemo(() => {
    return results.map((result) => toCallState(result, contract?.abi, methodName, latestBlockNumber))
  }, [contract?.abi, methodName, results, latestBlockNumber])
}

export function useMultipleContractSingleData(
  addresses: (string | undefined)[],
  abi: Abi,
  methodName: string,
  callInputs?: OptionalMethodInputs,
  options?: ListenerOptions,
): CallState[] {
  const callData: string | undefined = useMemo(
    () =>
      isValidMethodArgs(callInputs)
        ? encodeFunctionData({
            abi,
            functionName: methodName,
            args: toEncodeArgs(callInputs),
          })
        : undefined,
    [callInputs, abi, methodName],
  )

  const calls = useMemo(
    () =>
      addresses && addresses.length > 0 && callData
        ? addresses.map<Call | undefined>((address) => {
            return address && callData
              ? {
                  address,
                  callData,
                }
              : undefined
          })
        : [],
    [addresses, callData],
  )

  const results = useCallsData(calls, options)

  const latestBlockNumber = useBlockNumber()

  return useMemo(() => {
    return results.map((result) => toCallState(result, abi, methodName, latestBlockNumber))
  }, [abi, methodName, results, latestBlockNumber])
}

export function useSingleCallResult(
  contract: ReadContractBinding | null | undefined,
  methodName: string,
  inputs?: OptionalMethodInputs,
  options?: ListenerOptions,
): CallState {
  const calls = useMemo<Call[]>(() => {
    return contract && isValidMethodArgs(inputs)
      ? [
          {
            address: contract.address,
            callData: encodeFunctionData({
              abi: contract.abi,
              functionName: methodName,
              args: toEncodeArgs(inputs),
            }),
          },
        ]
      : []
  }, [contract, methodName, inputs])

  const result = useCallsData(calls, options)[0]
  const latestBlockNumber = useBlockNumber()

  return useMemo(() => {
    return toCallState(result, contract?.abi, methodName, latestBlockNumber)
  }, [result, contract?.abi, methodName, latestBlockNumber])
}
